import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { IonInput, IonPopover } from '@ionic/react'
import { cartMobx, restaurantConfig } from 'restaurant'
import { checkoutMobx, decorationMobx, memberMobx, storeMobx } from '@mobxStore'
import { useHistoryNew, confirmLayer } from '@utils'
import { validMobileOrMail } from '@dt/fe-utils'
import { Icon, UsernameInput } from '@cps'
import { LoginGuide } from '../../component'
import './index.scss'

export interface ICheckoutContactProps {
  hide?: boolean;
}

const CheckoutContact: React.FC<ICheckoutContactProps> = ({ hide }) => {
  /** 填写与未填写切换 */
  const [expand, setExpand] = useState(false)
  /** 是否有会员信息 */
  const [hasBenefits, setHasBenefits] = useState(false)
  /** tip 提示弹窗 */
  const [popoverState, setShowPopover] = useState({
    showPopover: '',
    event: undefined,
  })
  const {
    contactInfo,
    contactError,
    isSaveOrderInfo,
    neverShowMobileTips,
    setContactInfo,
    resetContactError,
    setIsSaveOrderInfo,
    setNeverShowMobileTips
  } = checkoutMobx
  const { setContact } = cartMobx
  const { eatTypeEntries, eatTypeEntriesMap } = decorationMobx
  const eatTypeEntriesValueArr = useMemo(() => {
    const arr: TEatType[] = eatTypeEntries.map(item => item.value)
    return arr
  }, [eatTypeEntries])
  const eatType = cartMobx.info.eatType

  const history = useHistoryNew()

  /** 切换授权调用cookie */
  const handleChangeIsSaveOrderInfo = () => {
    if (isSaveOrderInfo && memberMobx.isLogin) {
      confirmLayer({
        title: i18n.t('common.attention'),
        confirmText: i18n.t('mkt.common.confirm'),
        children: (
          <>
            <p>{i18n.t('checkout.clearOrderAfterCancel')}</p>
            {i18n.t('checkout.carefulOption')}
          </>
        ),
        cancelText: i18n.t('mkt.common.cancel'),
        className: 'cart-confirm-modal',
      })
        .then(() => {
          if (memberMobx.isLogin) {
            memberMobx.logout()
          }
          setIsSaveOrderInfo(!isSaveOrderInfo)
        })
        .catch(() => {})
      return
    }
    setIsSaveOrderInfo(!isSaveOrderInfo)
  }

  /** 非国内cookie缓存授权 */
  const _tipRender = () => (
    <div className='checkout-contact-tip start'>
      <button type='button' data-cypid='checkoutContactTip' onClick={handleChangeIsSaveOrderInfo}>
        <i
          className={`iconfont icon-checkmark--outline${
            isSaveOrderInfo ? ' checked' : ''
          }`}
        >
        </i>
        <span className='ml'>{i18n.t('checkout.saveOrderInfo')}</span>
      </button>
      <a
        className='ml link'
        data-cypid='checkoutContactTipShow'
        onClick={(e: any) => {
          e.persist()
          setShowPopover({ showPopover: 'tip', event: e })
        }}
      >
        {i18n.t('cart.details')}
      </a>
      <IonPopover
        cssClass='cart-tips-popover'
        event={popoverState.event}
        isOpen={popoverState.showPopover === 'tip'}
        onDidDismiss={() =>
          setShowPopover({ showPopover: '', event: undefined })}
      >
        <p>{i18n.t('checkout.saveOrderTips')}</p>
        <span
          data-cypid='checkoutContactTipHide'
          onClick={(e: any) => {
            e.persist()
            setShowPopover({ showPopover: '', event: undefined })
          }}
        >
          {i18n.t('newCart.know')}
        </span>
      </IonPopover>
    </div>
  )

  /** 判断是否没有填写 */
  const notFill = useMemo(() => {
    let result = false
    // 品牌码手机号和昵称都必须填（中国无需填写昵称）
    if (restaurantConfig.codeType === 'brand') {
      result = (window.bo !== 'cn' && !contactInfo.contactName) || !validMobileOrMail('mobile', contactInfo.contactMobile, contactInfo.countryCode)
    }
    // 门店码手机号和昵称都非必须填
    if (['table', 'store'].includes(restaurantConfig.codeType)) {
      result = (window.bo !== 'cn' && !contactInfo.contactName) || (contactInfo.contactMobile && !validMobileOrMail('mobile', contactInfo.contactMobile, contactInfo.countryCode))
    }
    return result
  }, [contactInfo])

  useEffect(() => {
    if (!(expand || notFill)) {
      !memberMobx.isLogin && checkHasBenefits()
    }
  }, [expand, notFill])

  useEffect(() => {
    if (!contactInfo.contactMobile) {
      setExpand(true)
    }
  }, [])

  const handleContentInfo = (data) => {
    checkoutMobx.setContactInfo({ ...data })
    cartMobx.setContact({ ...data })
  }

  useEffect(() => {
    if (memberMobx.isLogin) {
      setTimeout(() => {
        bo === 'cn' ? handleContentInfo({
          contactName: memberMobx.memberInfo.nickName || contactInfo.contactName,
          contactMobile: memberMobx.memberInfo.mobile || contactInfo.contactMobile,
          countryCode: memberMobx.memberInfo.countryCode ? String(memberMobx.memberInfo.countryCode) : contactInfo.countryCode
        }) : handleContentInfo({
          contactName: contactInfo.contactName || memberMobx.memberInfo.nickName,
          contactMobile: contactInfo.contactMobile || memberMobx.memberInfo.mobile,
          countryCode: (!contactInfo.contactMobile || !contactInfo.countryCode) && memberMobx.memberInfo.countryCode ? String(memberMobx.memberInfo.countryCode) : contactInfo.countryCode
        })
      })
    }
  }, [memberMobx.memberInfo])

  useEffect(() => {
    if (!contactInfo.contactMobile) {
      handleContentInfo({
        ...contactInfo,
        countryCode: String(window.bo === 'cn' ? 86 : window.bo === 'us' ? 1 : 49)
      })
    }
  }, [])

  if (hide) {
    return null
  }

  /** 验证用户填写信息是否存在会员中 */
  const checkHasBenefits = () => {
    contactInfo.countryCode && contactInfo.contactMobile && memberMobx.checkHasBenefits({
      mobile: contactInfo.contactMobile,
      countryCode: parseInt(contactInfo.countryCode)
    }).then(res => {
      setHasBenefits(!!res?.isMember && !!res?.hasBenefits)
    }).catch(e => {
      setHasBenefits(false)
    })
  }

  /** 会员登录入口 */
  const hasBenefitsContentReander = (position: 'top' | 'middle') => {
    return (
      <div className={`login-guide-wrapper benefits ${position}`} data-cypid='checkoutContactLogin' onClick={() => history.push({ pathname: '/login', state: { countryCode: contactInfo.countryCode, contactMobile: contactInfo.contactMobile } })}>
        <div className='login-guide-info'>{i18n.t('checkout.hasBenefits')}</div>
        <i className='iconfont icon-arrows-right'></i>
      </div>
    )
  }

  /** 强化留手机提示 */
  const emptyMobileTips = () => {
    return (!hasBenefits && !neverShowMobileTips && !contactInfo.contactMobile) && (
      <div className='empty-mobile-tips-wrapper'>
        <div className='empty-mobile-tips-wrap'>
          <div className='left-wrap'>
            <div className='arrow'>👆</div>
            <span className='txt'>{i18n.t('checkout.whatContactInfoTip')}</span>
          </div>
          <div className='right-wrap' data-cypid='checkoutContactMobileTips' onClick={setNeverShowMobileTips}>{i18n.t('checkout.neverTip')}</div>
        </div>
      </div>
    )
  }

  /** 用户信息未填写 */
  const _expandRender = () => (
    <div className='checkout-contact-container'>
      <header>
        <strong>{i18n.t('checkout.contactInfo')}</strong>
      </header>
      <small style={{ color: '#7A7A7A' }}>
        {i18n.t('checkout.whatContactInfo')}
      </small>
      <div className='checkout-contact-form'>
        {window.bo !== 'cn' && (
          <>
            <div
              className={`checkout-contact-form-item${
                contactError.nameError ? ' error-form-item' : ''
              }`}
            >
              <span className='checkout-contact-form-item-icon'>
                <i className='iconfont icon-my-circle'></i>
              </span>
              <IonInput
                value={contactInfo.contactName}
                placeholder={i18n.t('mkt.common.tipNickName')}
                onIonChange={({ detail: { value } }) => {
                  handleContentInfo({
                    ...contactInfo,
                    contactName: value,
                  })
                  resetContactError()
                }}
              />
            </div>
            <div
              className='checkout-contact-error show'
              style={{ display: contactError.nameError ? 'block' : 'none' }}
            >
              <span>{i18n.t('checkout.howICallYou')}</span>
            </div>
          </>
        )}
        <div
          className={`checkout-contact-form-item${
            contactError.mobileError ? ' error-form-item' : ''
          }`}
        >
          <UsernameInput
            value={{
              pass: contactInfo.contactMobile,
              countryCode: parseInt(contactInfo.countryCode),
            }}
            inputType='mobile'
            placeholder={checkoutMobx.mustMobileInput ? i18n.t('mkt.common.tipMobile') : i18n.t('mkt.common.tipMobileUn')}
            onChange={(data) => {
              handleContentInfo({
                ...contactInfo,
                contactMobile: data.pass,
                countryCode: String(data.countryCode),
              })
            }}
            onBlur={checkHasBenefits}
          />
        </div>
        {!window.MEMBERISBUSY && !memberMobx.isLogin && contactInfo.countryCode && contactInfo.contactMobile && hasBenefits && (
          hasBenefitsContentReander('middle')
        )}
        <div
          className='checkout-contact-error show'
          style={{ display: contactError.mobileError ? 'block' : 'none' }}
        >
          <span>{i18n.t('checkout.plInputRightMobile')}</span>
        </div>
        {emptyMobileTips()}
        {window.bo !== 'cn' && !memberMobx.isLogin ? _tipRender() : null}
      </div>
    </div>
  )

  /** 已填写状态 */
  const _unExpandRender = () => (
    <div className='checkout-contact-bar' data-cypid='checkoutContactExpand' onClick={() => setExpand(true)}>
      <div className='checkout-contact-bar-title'>
        {window.bo !== 'cn' && contactInfo.contactName && <span>{contactInfo.contactName}</span>}
        {contactInfo.contactMobile && <span>{contactInfo.contactMobile}</span>}
        <i className='iconfont icon-edit1'></i>
      </div>
      {/* {
        !contactInfo.contactName && !contactInfo.contactMobile ? (
          <small style={{ color: '#7A7A7A' }}>
            {i18n.t('checkout.whatContactInfo')}
          </small>
        ) : null
      } */}
      <small style={{ color: '#7A7A7A' }}>
        {i18n.t('checkout.whatContactInfo')}
      </small>
      {/* <div className='checkout-contact-edit' onClick={() => setExpand(true)}>
        <i className='iconfont icon-edit1'></i>
      </div> */}
    </div>
  )

  return (
    <div className='checkout-contact-wrapper'>
      {
        contactInfo.countryCode && contactInfo.contactMobile ? (hasBenefits ? (!window.MEMBERISBUSY && !memberMobx.isLogin && (!(expand || notFill)) && (
          hasBenefitsContentReander('top')
        )) : <LoginGuide isLogin={memberMobx.isLogin} />) : (
          <LoginGuide isLogin={memberMobx.isLogin} />
        )
      }
      {(expand || notFill) ? _expandRender() : _unExpandRender()}
      <div className='checkout-shopinfo-wrapper'>
        <Icon name='mendian' />
        <span className='store-name'>{storeMobx.info.storeName}</span>
        <div className='store-tag'>
          {
            eatTypeEntries.length < 2 || !eatTypeEntriesValueArr.includes(eatType) ? (
              <span className='store-eat-tag'>
                {eatTypeEntriesMap[eatType]?.name || i18n.t(`entry.${eatType}`)}
              </span>
            ) : <></>
          }
        </div>
      </div>
    </div>
  )
}

export default observer(CheckoutContact)
